<template>
  <InfuseTable
    :active="(row, id) => active(row, id)"
    :columns="columns"
    :has-more-items="hasMoreItems"
    :loading="loading"
    :search="search"
    :filters="filters"
    :rows="rows"
    :removable="removable"
    :remove-permission-id="commandPermissions.REMOVE_CHARGING_STATION_FROM_LOCATION"
    :selectable="enableFirmwareUpdate"
    @click-row="(row) => emit('clickRow', row)"
    @load="emit('load')"
    @remove="(row) => emit('remove', row)"
    @remove:filter="(f: Filter) => emit('remove:filter', f)"
    @search="(query: string) => emit('search', query)"
    @selected="onSelected"
    @sort="(sort) => emit('sort', sort)"
  >
    <template #actionsStart>
      <div v-if="filterConfigs?.length" ref="showFiltersDiv" class="w-48">
        <InfuseButton class="w-full px-2" color="secondary" size="medium" @click="showFilters = !showFilters">
          <div class="flex items-center justify-center gap-1.5">
            <font-awesome-icon :icon="['fas', 'arrow-down-wide-short']" />
            <span>{{ t('chargingStation.action.addFilter') }}</span>
          </div>
        </InfuseButton>

        <InfuseFilterDropdown
          v-if="showFilters"
          :model-value="filterConfigs || []"
          :ignore-close-on-click="showFiltersDiv ? [showFiltersDiv] : []"
          @add:filter="(f: Filter) => emit('add:filter', f)"
          @close="showFilters = false"
        />
      </div>
    </template>

    <template #actions>
      <slot name="actions" />
    </template>

    <template #selectionActions="slotProps">
      <slot name="selectionActions" v-bind="slotProps" />
    </template>

    <template #deviceId="{ data, row }">
      <div class="flex flex-row gap-2">
        <div class="flex flex-col gap-2 justify-center">
          <b>{{ data }}</b>
          <div class="font-normal" v-if="row.customIdentifier">{{ row.customIdentifier }}</div>
        </div>
      </div>
    </template>

    <template #status="{ data }">
      <ChargingStationTag :status="data" />
    </template>

    <template #type="{ data, active }">
      <div v-if="data && data.model && (showChargingStationVendor ? data.vendor : true)">
        <div class="flex flex-col gap-1 justify-center">
          <span class="float-left w-fit" :class="active ? 'text-primary-500' : 'text-neutral-500'"
            v-if="showChargingStationVendor">
            {{ data.vendor }}
          </span>
          <span class="font-semibold" :class="active ? 'text-primary-500' : 'text-neutral-500'">{{ displayChargingStationType(data) }}</span>
        </div>
      </div>
      <div v-else>&nbsp;</div>
      <!-- show a space when no data is available -->
    </template>

    <template #firmware="{ data, active }">
      <InfuseTag v-if="data" :variant="active ? 'success' : 'neutral'">{{ data }}</InfuseTag>
    </template>

    <template #lastSeen="{ data, active }">
      <span :class="active ? 'text-primary-500' : 'text-neutral-500'">{{ formatDate(data) }}</span>
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { commandPermissions } from '@infuse/api/cpms/operator';
import { type ChargingStation, displayChargingStationType } from '@infuse/cpms/charging-station/store';

import { InfuseButton, InfuseFilterDropdown, InfuseTable, InfuseTag } from '@infuse/ui/components';
import type { ColumnSort, Filter, FilterConfiguration, UiTableColumn } from '@infuse/ui/domain';
import { formatDate } from '@infuse/ui/utils';
import { useAppSettingsStore } from '@infuse/utils';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ChargingStationTag from './ChargingStationTag.vue';

const { t } = useI18n();
const appSettingsStore = useAppSettingsStore();
const enableFirmwareUpdate = computed(
  () => props.enableSelection ?? appSettingsStore.getAsBool('VITE_ENABLE_FIRMWARE_UPDATE_PREVIEW')
);

interface Emits {
  (e: 'add:filter', filter: Filter): void;
  (e: 'clickRow', row: ChargingStation): void;
  (e: 'load'): void;
  (e: 'remove', row: ChargingStation): void;
  (e: 'remove:filter', filter: Filter): void;
  (e: 'search', query: string): void;
  (e: 'selected', rows: ChargingStation[]): void;
  (e: 'sort', sort: ColumnSort): void;
}

function onSelected(value: ChargingStation[]) {
  emit('selected', value);
}

const emit = defineEmits<Emits>();

interface Props {
  active?: (row: ChargingStation, id?: number) => boolean;
  enableSelection?: boolean;
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
  hasMoreItems: boolean;
  loading: boolean;
  page: number;
  removable?: boolean;
  rows: ChargingStation[];
  searchEnabled: boolean;
  searchQuery?: string;
  showChargingStationVendor?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  active: () => false,
  enableSelection: undefined,
  hasMoreItems: true,
  loading: false,
  removable: false,
  rows: () => [],
  searchEnabled: true,
  searchQuery: '',
  // Showing the vendor next to the model is a product variant (some products show model only).
  // Default from the variant rather than `true`, so consumers that don't pass the prop
  // still render the product's intended column. Inlined because `withDefaults` is
  // hoisted out of `setup()` and cannot reference a local constant.
  showChargingStationVendor: import.meta.env.VITE_UI_VARIANT_SHOW_CHARGING_STATION_VENDOR !== 'false'
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('chargingStation.search.placeholder'),
        query: props.searchQuery
      }
);

const showFiltersDiv = ref<HTMLElement | null>(null);
const showFilters = ref<boolean>(false);

const columns: UiTableColumn[] = [
  {
    name: 'deviceId',
    label: () => t('chargingStation.table.header.deviceId'),
    field: 'id',
    bold: true
  },
  {
    name: 'status',
    label: () => t('chargingStation.table.header.status'),
    field: 'friendlyStatus'
  },
  {
    name: 'type',
    label: () => props.showChargingStationVendor ? t('chargingStation.table.header.model') : t('chargingStation.table.header.modelOnly'),
    field: 'type'
  },
  {
    name: 'firmware',
    label: () => t('chargingStation.table.header.firmware'),
    field: 'firmwareVersion'
  },
  {
    name: 'lastSeen',
    label: () => t('chargingStation.table.header.lastSeen'),
    field: 'lastSeen',
    sortable: true
  }
];
</script>
